// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import moment, { Moment } from 'moment';
import { DeepReadonly } from 'src/app/modules/shared/utils/api-call-state.util';
import { PatternMatchModelWithId } from '../../services/pattern-match-data/pattern-match-data.service';
import { Bin, Item } from './pm-chart.component';

const keyDateFormat = 'YYYY-MM-DD';
export interface Options {
  // if true only a merged bar is created from the first item
  mergeBins?: boolean;

  // if set, all other patches will be transparent!
  focusId?: number;
  focusDate?: Moment;

  ascending?: boolean;

  // will clammp data to date range
  fixedDateRange?: { min: Date; max: Date };

  // will insert empty dates if required
  addDatesInDateRange?: boolean;
}

/**
 * aggregates bins per day in a certain range.
 * also returns the original matches clamped by that range!
 */
export function aggregateBinsFromAnalysis(
  patternMatches: DeepReadonly<PatternMatchModelWithId[]>,
  options: Options = {
    mergeBins: false,
    addDatesInDateRange: true,
  },
): { bins: Bin[]; patternMatches: DeepReadonly<PatternMatchModelWithId[]> } {
  const { ascending, fixedDateRange, addDatesInDateRange } = options;
  const bins: Record<string, Bin> = {};

  if (fixedDateRange && addDatesInDateRange) {
    let day = moment(fixedDateRange.min);
    const end = moment(fixedDateRange.max).add(1, 'day');
    while (!day.isSame(end, 'day')) {
      const date = day;
      const key = date.format(keyDateFormat);
      bins[key] = {
        date: date.toDate(),
        items: [],
      };
      day = day.add(1, 'day');
    }
  }

  let allUsedMatches: PatternMatchModelWithId[] = [];
  patternMatches.forEach((patternMatch) => {
    allUsedMatches = [
      ...allUsedMatches,
      ...processPatternMatch(patternMatch, bins, options),
    ];
  });

  return {
    bins: Object.values(bins).sort((a, b) => {
      return (ascending ? -1 : 1) * (b.date.getTime() - a.date.getTime());
    }),
    patternMatches: allUsedMatches,
  };
}

/**
 * processes one pattern match
 */
function processPatternMatch(
  patternMatch: DeepReadonly<PatternMatchModelWithId>,
  bins: Record<string, Bin>, // mutated in place
  { mergeBins, focusId, focusDate, fixedDateRange }: Options = {
    mergeBins: false,
    addDatesInDateRange: true,
  },
) {
  const usedMatches: PatternMatchModelWithId[] = [];
  const disabledByPattern = !!(
    focusId !== undefined && patternMatch.id !== focusId
  );
  // clamped down by date range
  const findingsWithinRange: {
    date: string;
    occurrence: number;
  }[] = [];
  patternMatch.matchFindings
    .filter((match) => {
      const day = moment(match.date);
      if (fixedDateRange) {
        if (
          day.isBefore(fixedDateRange.min) ||
          day.isAfter(fixedDateRange.max)
        ) {
          // skip if out of bounds
          return false;
        }
      }
      return true;
    })
    .forEach((match) => {
      const day = moment(match.date);
      findingsWithinRange.push(match);
      const disabled =
        disabledByPattern || !!(focusDate && !focusDate?.isSame(day, 'day'));
      const dayKey = day.format(keyDateFormat);
      const item: Item = {
        count: match.occurrence,
        label: 'Matches', //patternMatch.componentFilterName,
        raw: patternMatch,
        disabled,
      };
      const bin =
        bins[dayKey] ??
        (bins[dayKey] = {
          date: day.toDate(),
          items: [],
        });
      if (mergeBins) {
        if (bin.items[0]) {
          bin.items[0].count += item.count;
        } else {
          bin.items[0] = item;
        }
        return;
      }
      bin.items.push(item);
    });
  if (findingsWithinRange.length > 0) {
    usedMatches.push(
      structuredClone({
        ...patternMatch,
        matchFindings: findingsWithinRange,
      }) as Readonly<PatternMatchModelWithId>,
    );
  }
  return usedMatches;
}
